<template>
<div class="filters-container">
  <div class="filters" :class="{ 'filters--modal': isModal }">
    <transition name="fade">
      <Modal class="filters-modal" v-show="isModal" @close="isModal = false">
        <div class="filters-modal-wrapper">
          <div class="filters-modal__header">
            <div class="font-caps" v-text="modalFilter.label" />
            <div class="filters-modal__header__icon" @click="isModal = false">
              <Icon icon="close" color="#9999a9" />
            </div>
          </div>

          <div class="filters-modal__body">
            <div
              class="filters__item__checks__item"
              :class="{ disabled: check.disabled }"
              v-for="(check, idx) in modalFilter.checks"
              :key="idx"
              @click.stop="setCheckValue(check)"
            >
              <Checkbox :checked="check.selected" :disabled="check.disabled" />
              <div class="font-text-small" v-text="check.title" />
            </div>
          </div>
        </div>
      </Modal>
    </transition>

    <div class="filters__header">
      <div class="filters__header__back" @click="$emit('closeFilter')">
        <Icon icon="back" color="#121212" />
      </div>
      <div
        class="filters__header__text font-h2"
        v-text="$t('serp.filters.trigger')"
      />
    </div>

    <template v-for="(filter, id) in clientFilters">
      <div
        class="filters__item"
        v-if="!isHiddenFilter(id)"
        :key="id"
        :class="{
          '_popup-highlight': id === 'only_private' && popups.individual,
        }"
      >
        <!-- range filter -->
        <div
          :ref="'ranged' + id"
          class="filters__item__range"
          v-if="filter.type === 'range'"
        >
          <div
            class="filters__item__dropdown"
            :class="{ wrapped: !filter.visible }"
            @click="filter.visible = !filter.visible"
          >
            <div class="font-text" v-text="filter.label" />

            <div class="filters__item__dropdown__reset">
              <div
                class="font-text filters__item__reset"
                v-show="changedFilters.includes(id)"
                v-text="$t('serp.filters.reset')"
                @click.stop="resetFilter(id)"
              />

              <div
                class="filters__item__dropdown__icon"
                :class="{
                  'filters__item__dropdown__icon--active': filter.visible,
                }"
              >
                <Icon icon="dropdown" color="#C1CEDE" />
              </div>
            </div>
          </div>

          <div
            class="filters__item__dropdown__menu"
            :class="{
              'filters__item__dropdown__menu--visible-range': filter.visible,
            }"
          >
            <div class="filters__item__range__inputs">
              <Input
                class="filters__item__range__inputs__left"
                :label="filter.labelStart"
                :placeholder="filter.min"
                :value="id === 'prices' ? pricesValue[0] : durationValue[0]"
                @input="setInputValue(id, $event, 0)"
              />
              <Input
                class="filters__item__range__inputs__right"
                :label="filter.labelEnd"
                :placeholder="filter.max"
                :value="id === 'prices' ? pricesValue[1] : durationValue[1]"
                @input="setInputValue(id, $event, 1)"
              />
            </div>

            <Range
              :ref="'range' + id"
              :min="filter.min"
              :max="filter.max"
              :value="id === 'prices' ? pricesValue : durationValue"
              @input="setRangeValue(id, $event)"
              @drag-end="triggerRange(id)"
            />
          </div>
        </div>

        <!-- checks filter -->
        <div class="filters__item__checks" v-if="filter.type === 'checks'">
          <div
            class="filters__item__dropdown"
            :class="{ wrapped: !filter.visible }"
            @click="filter.visible = !filter.visible"
          >
            <div class="font-text" v-text="filter.label" />

            <div class="filters__item__dropdown__reset">
              <div
                class="font-text filters__item__reset"
                v-show="changedFilters.includes(id)"
                v-text="$t('serp.filters.reset')"
                @click.stop="resetFilter(id)"
              />

              <div
                class="filters__item__dropdown__icon"
                :class="{
                  'filters__item__dropdown__icon--active': filter.visible,
                }"
              >
                <Icon icon="dropdown" color="#C1CEDE" />
              </div>
            </div>
          </div>

          <div
            class="filters__item__dropdown__menu"
            :class="{
              'filters__item__dropdown__menu--visible': filter.visible,
            }"
          >
            <div
              class="filters__item__checks__item"
              :class="{ disabled: check.disabled }"
              v-for="(check, idx) in filter.checks.slice(0, 5)"
              :key="idx"
              @click.stop="setCheckValue(check)"
            >
              <Checkbox :checked="check.selected" :disabled="check.disabled" />
              <div class="font-text-small" v-text="check.title" />
            </div>

            <Button
              v-if="filter.checks.length > 5"
              class="filters__item__checks__button"
              :color="'gray'"
              :text="`${$t('serp.filters.allChecks' + id)} (${
                filter.checks.length
              })`"
              @click="showModal({ filterId: id })"
            />
          </div>
        </div>

        <!-- switch filter -->
        <div
          class="filters__item__switch filters-uniq-serp__item__switch"
          v-if="filter.type === 'switch'"
          @click="filter.value = !filter.value"
        >
          <div class="filters__item__switch__label">
            <div
              class="font-text switch-filter__uniq-serp-class"
              v-text="filter.label"
            ></div>
            <div
              v-if="filter.helperText"
              class="filters__item__helper font-text-small"
            >
              <div class="filters__item__helper-icon">
                <Icon icon="info" color="#828296" />
              </div>
              <div
                class="filters__item__helper-modal"
                v-text="filter.helperText"
              ></div>
            </div>
          </div>
          <Switcher :checked="filter.value" />
        </div>

        <!-- button-select filter -->
        <div
          class="filters__item__buttons-select"
          v-if="filter.type === 'buttons-select'"
        >
          <div
            class="filters__item__dropdown"
            :class="{ wrapped: !filter.visible }"
            @click="filter.visible = !filter.visible"
          >
            <div class="font-text" v-text="filter.label" />

            <div class="filters__item__dropdown__reset">
              <div
                class="font-text filters__item__reset"
                v-show="changedFilters.includes(id)"
                v-text="$t('serp.filters.reset')"
                @click.stop="resetFilter(id)"
              />

              <div
                class="filters__item__dropdown__icon"
                :class="{
                  'filters__item__dropdown__icon--active': filter.visible,
                }"
              >
                <Icon icon="dropdown" color="#C1CEDE" />
              </div>
            </div>
          </div>

          <div
            class="filters__item__dropdown__menu"
            :class="{
              'filters__item__dropdown__menu--visible': filter.visible,
            }"
          >
            <div class="filters__item__buttons-select__buttons">
              <ButtonSelect
                v-for="(button, idx) in filter.buttons"
                :text="button.label"
                :active="filter.value && filter.value.label === button.label"
                :key="idx"
                @click="setButtonSelect(filter, button)"
              />
            </div>
          </div>
        </div>

        <!-- input filter -->
        <div class="filters__item__input" v-if="filter.type === 'input'">
          <div
            class="filters__item__dropdown"
            :class="{ wrapped: !filter.visible }"
            @click="filter.visible = !filter.visible"
          >
            <div class="font-text" v-text="filter.label" />

            <div class="filters__item__dropdown__reset">
              <div
                class="font-text filters__item__reset"
                v-show="changedFilters.includes(id)"
                v-text="$t('serp.filters.reset')"
                @click.stop="resetFilter(id)"
              />

              <div
                class="filters__item__dropdown__icon"
                :class="{
                  'filters__item__dropdown__icon--active': filter.visible,
                }"
              >
                <Icon icon="dropdown" color="#C1CEDE" />
              </div>
            </div>
          </div>

          <div
            class="filters__item__dropdown__menu"
            :class="{
              'filters__item__dropdown__menu--visible': filter.visible,
            }"
          >
            <Input
              :label="filter.inputLabel"
              :type="'number'"
              :numberBtns="true"
              :min=" 1"
              :max="filter.max"
              v-model="filter.value"
            />
          </div>
        </div>

        <!-- radios filter -->
        <div class="filters__item__radios" v-if="filter.type === 'radios'">
          <div
            class="filters__item__dropdown"
            :class="{ wrapped: !filter.visible }"
            @click="filter.visible = !filter.visible"
          >
            <div class="font-text" v-text="filter.label" />

            <div
              class="filters__item__dropdown__icon"
              :class="{
                'filters__item__dropdown__icon--active': filter.visible,
              }"
            >
              <Icon icon="dropdown" color="#C1CEDE" />
            </div>
          </div>

          <div
            class="filters__item__dropdown__menu"
            :class="{
              'filters__item__dropdown__menu--visible': filter.visible,
            }"
          >
            <div
              class="filters__item__radios__item"
              v-for="(radio, idx) in filter.radios"
              :key="idx"
              @click="filter.value = radio"
            >
              <Radio
                class="filters__item__radios__radio"
                :checked="filter.value && filter.value.label === radio.label"
              />

              <div
                class="filters__item__radios__rating font-caps"
                v-if="+radio.min"
                v-text="radio.min"
              />

              <div class="font-text-small" v-text="radio.label" />
            </div>
          </div>
        </div>

        <!-- select filter -->
        <div class="filters__item__select" v-if="filter.type === 'select'">
          <div
            class="filters__item__dropdown"
            :class="{ wrapped: !filter.visible }"
            @click="filter.visible = !filter.visible"
          >
            <div class="font-text" v-text="filter.label" />

            <div class="filters__item__dropdown__reset">
              <div
                class="font-text filters__item__reset"
                v-show="changedFilters.includes(id)"
                v-text="$t('serp.filters.reset')"
                @click.stop="resetFilter(id)"
              />

              <div
                class="filters__item__dropdown__icon"
                :class="{
                  'filters__item__dropdown__icon--active': filter.visible,
                }"
              >
                <Icon icon="dropdown" color="#C1CEDE" />
              </div>
            </div>
          </div>

          <div
            class="filters__item__dropdown__menu"
            :class="{
              'filters__item__dropdown__menu--visible': filter.visible,
            }"
          >
            <div
              class="filters__item__helper-text font-text-small"
              v-text="filter.helperText"
            ></div>

            <yt-input-select
              v-model="filter.value"
              :options="filter.options"
              :placeholder="filter.placeholder"
              :allow-empty="true"
              track-by="id"
              label="label"
            />
          </div>
        </div>
      </div>
    </template>

    <Button
      class="filters__button"
      :text="$t('serp.filters.showTours')"
      :hidden="true"
      @click="$emit('closeFilter')"
    />

    <popup-individual-suggest v-model="popups.individual" />
  </div>
    <div>
      <slot name="banner"></slot>
    </div>
</div>
</template>

<script>
import Icon from "@/components/icons/index.vue";
import Checkbox from "@/components/ui-kit/checkbox/index.vue";
import Input from "@/components/ui-kit/input/index.vue";
import Range from "@/components/ui-kit/input-range/index.vue";
import Switcher from "@/components/ui-kit/switch/index.vue";
import Button from "@/components/ui-kit/button/index.vue";
import ButtonSelect from "@/components/ui-kit/button-select/index.vue";
import Radio from "@/components/ui-kit/radio/index.vue";
import YtInputSelect from "@/components/ui-kit/yt-input-select/index.vue";
import Modal from "@/components/ui-kit/modal/index.vue";
import PopupIndividualSuggest from "@/components/organisms/popup-individual-suggest/index.vue";
import helper from "@/mixins/helper.js";

import { mapActions, mapMutations, mapState } from "vuex";
import { format, parse, startOfMonth, endOfMonth } from "date-fns";
import { debounce } from "debounce";
import getBackendErrorMessage from "@/assets/js/utils/get-backend-error-message";
import Cookies from "js-cookie";
import { i18n } from "@/plugins/i18n";
import { useSerp } from "@/state/serp";
import { usePageMeta } from "@/state/page-meta";
import { useQueryString } from "@/utils/query-string";
import { abTestController } from "@/plugins/abtest";

export default {
  name: "filters",
  mixins: [helper],
  components: {
    Icon,
    Checkbox,
    Input,
    Range,
    Switcher,
    Button,
    ButtonSelect,
    Radio,
    YtInputSelect,
    Modal,
    PopupIndividualSuggest,
  },
  props: {
    sortQuery: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      modalFilterId: null,
      isModal: false,
      // maybe change structure of clientFilters for better query params build
      clientFilters: {
        duration: {
          label: this.$t("serp.filters.duration"),
          labelStart: this.$t("serp.filters.durationFrom"),
          labelEnd: this.$t("serp.filters.durationTo"),
          type: "range",
          value: [],
          min: 0,
          max: 100,
          visible: true,
        },
        types: {
          label: this.$t("serp.filters.travelType"),
          type: "checks",
          checks: [],
          visible: false,
        },
        prices: {
          label: this.$t("serp.filters.price"),
          labelStart: this.$t("serp.filters.from"),
          labelEnd: this.$t("serp.filters.to"),
          type: "range",
          value: [],
          min: 0,
          max: 1000000,
          visible: true,
        },
        spaces: {
          label: this.$t("serp.filters.spaces"),
          inputLabel: this.$t("serp.filters.spacesHelper"),
          type: "input",
          value: 1,
          visible: false,
        },
        group_size: {
          label: this.$t("serp.filters.maxSpaces"),
          inputLabel: this.$t("serp.filters.spacesHelper"),
          type: "input",
          value: 0,
          min: 1,
          max: 100,
          visible: false,
        },
        ratings: {
          label: this.$t("serp.filters.rating"),
          type: "radios",
          visible: false,
          value: {
            label: this.$t("serp.filters.ratingChecks[0]"),
            min: null,
            max: null,
          },
          radios: [
            {
              label: this.$t("serp.filters.ratingChecks[0]"),
              min: null,
              max: null,
            },
            {
              label: this.$t("serp.filters.ratingChecks[1]"),
              min: "4.5",
              max: "5.0",
            },
            {
              label: this.$t("serp.filters.ratingChecks[2]"),
              min: "4.0",
              max: "5.0",
            },
            {
              label: this.$t("serp.filters.ratingChecks[3]"),
              min: "3.5",
              max: "5.0",
            },
            {
              label: this.$t("serp.filters.ratingChecks[4]"),
              min: "3.0",
              max: "5.0",
            },
          ],
        },
        guarantees: {
          label: this.$t("serp.filters.guarantees"),
          helperText: this.$t("serp.filters.guaranteesHelper"),
          type: "switch",
          value: abTestController.getSegment(14939) === 'B',
        },
        comforts: {
          label: this.$t("serp.filters.comfort"),
          type: "checks",
          checks: [
            {
              key: "0",
              title: this.$t("serp.filters.comfortChecks[0]"),
            },
            {
              key: "1",
              title: this.$t("serp.filters.comfortChecks[1]"),
            },
            {
              key: "2",
              title: this.$t("serp.filters.comfortChecks[2]"),
            },
            {
              key: "3",
              title: this.$t("serp.filters.comfortChecks[3]"),
            },
            {
              key: ["4", "5"],
              title: this.$t("serp.filters.comfortChecks[4]"),
            },
          ],
          visible: false,
        },
        ages: {
          label: this.$t("serp.filters.ages"),
          type: "buttons-select",
          value: null,
          buttons: [
            { label: "18-39", min: 18, max: 39 },
            { label: "30+", min: 30, max: null },
            { label: "40+", min: 40, max: null },
            { label: "50+", min: 50, max: null },
            { label: "60+", min: 60, max: null },
            { label: "70+", min: 70, max: null },
          ],
          visible: false,
          placeholder: this.$t("serp.filters.agesPlaceholder"),
          input: {
            value: "",
            options: {
              label: this.$t("serp.filters.agesPlaceholder"),
              inputType: "number",
              numberBtns: true,
            },
          },
        },
        children: {
          label: this.$t("serp.filters.children"),
          helperText: this.$t("serp.filters.childrenHelper"),
          type: "select",
          value: null,
          options: [
            { id: null, label: this.$t("serp.filters.childrenAges.all") },
            ...Array(18)
              .fill(1)
              .map((value, id) => ({
                id,
                label: this.$tc("serp.filters.childrenAges.year", id),
              })),
          ],
          placeholder: this.$t("serp.filters.childrenPlaceholder"),
          visible: false,
        },
        activities: {
          label: this.$t("serp.filters.activities"),
          type: "checks",
          checks: [
            {
              key: "1",
              title: this.$t("serp.filters.activitiesChecks[0]"),
            },
            {
              key: "2",
              title: this.$t("serp.filters.activitiesChecks[1]"),
            },
            {
              key: "3",
              title: this.$t("serp.filters.activitiesChecks[2]"),
            },
            {
              key: "4",
              title: this.$t("serp.filters.activitiesChecks[3]"),
            },
            {
              key: "5",
              title: this.$t("serp.filters.activitiesChecks[4]"),
            },
          ],
          visible: false,
        },
        only_private: {
          label: this.$t("serp.filters.private"),
          helperText: this.$t("serp.filters.privateHelper"),
          type: "switch",
          value: false,
        },
        languages: {
          label: this.$t("serp.filters.languages"),
          type: "checks",
          checks: [],
          visible: false,
        },
        have_installment: {
          label: this.$t("serp.filters.installment"),
          type: "switch",
          value: false,
        },
        discount: {
          label: this.$t("serp.filters.discount"),
          type: "switch",
          value: false,
        },
        // have_cashback: {
        //   label: this.$t("serp.filters.cashback"),
        //   helperText: this.$t("serp.filters.cashbackHelper"),
        //   type: "switch",
        //   value: false,
        // },
      },
      isFetching: false,
      initCheckFilterId: null,
      initFilters: {},
      durationValue: [],
      pricesValue: [],
      hiddenFilters: "",
      pathname: "",
      popups: {
        individual: false,
      },
      checks: {
        comforts: [],
        activities: [],
        types: [],
        languages: [],
      },
      rightCheck: "",
      firstPageLoad: false,
    };
  },
  computed: {
    ...mapState({
      currency: (state) => state.currency.currency,
      currencyChar: (state) => state.currency.currency.title.charAt(0),
      language: (state) => state.language.language,
      query: (state) => state.serp.serpQuery.query,
      facets: (state) => state.serp.serpTours.facets?.data,
      initFacets: (state) => state.serp.serpTours.initFacets?.data,
      strictPeriod: (state) => state.serp.strictPeriod,
      dates: (state) => state.serp.dates,
      location: (state) => state.serp.location,
      only_exclusive: (state) => state.serp.only_exclusive,
      productType: (state) => state.serp.productType,

    }),
    modalFilter() {
      return this.clientFilters?.[this.modalFilterId] || {};
    },
    isOneDayToursPage() {
      return this.productType === 'oneDay';
    },
    isExclusiveToursPage() {
      return this.productType === 'exclusive';
    },
    // TODO: build query params convinient way
    filtersQuery() {
      const { stringifyQuery } = useQueryString();

      let query = {
        is_period_strict: +this.strictPeriod,
        currency: this.currency.id.toLowerCase(),
        available_spaces: this.clientFilters.spaces.value,
        group_size: this.clientFilters.group_size.value,
        only_en: i18n.locale === "en" ? "1" : "0",
        lang: i18n.locale,
        ...this.hiddenFilters
      };

      switch (this.productType) {
        case 'oneDay':
          query.duration = {};
          query.duration.from = 1;
          query.duration.to = 1;
          break;
        case 'exclusive':
          query.only_exclusive = 1;
          break;
        case 'multiDays':
        default:
          query.duration = {};
          query.duration.from = 1;
          break;
      }

      if (this.location) {
        query.loc = {};

        this.location.forEach((location, idx) => {
          const loc = {};

          if (location.type === "distance") {
            loc.type = location.type;
            loc.lat = location.lat;
            loc.lon = location.lon;
            loc.radius = location.radius;
          } else {
            loc.type = location.type;
            loc.id = location.id;
          }

          query.loc[idx] = loc;
        });
      }

      if (this.dates) {
        query.period = {};

        this.dates.forEach(([dateFrom, dateTo, dateType], idx) => {
          const period = {};
          const from = format(dateFrom, "dd.MM.yyyy");
          const to = format(dateTo, "dd.MM.yyyy");

          if (dateType === "month") {
            period.month = from;
          } else {
            period.from = from;
            period.to = to;
          }

          query.period[idx] = period;
        });
      }

      if (this.clientFilters) {
        Object.entries(this.clientFilters).forEach(([key, filter]) => {
          const changed = this.changedFilters.includes(key);

          if (changed) {
            switch (key) {
              case "types":
                query.types = [];

                filter.checks
                  .filter(({ selected }) => selected)
                  .forEach(({ key }) => {
                    query.types.push(key);
                  });
                break;
              case "activities":
                query.activity = [];

                filter.checks
                  .filter(({ selected }) => selected)
                  .forEach(({ key }) => {
                    query.activity.push(key);
                  });
                break;
              case "comforts":
                query.comfort = [];

                filter.checks
                  .filter(({ selected }) => selected)
                  .forEach(({ key }) => {
                    if (Array.isArray(key)) {
                      key.forEach((item) => {
                        query.comfort.push(item);
                      });
                    } else {
                      query.comfort.push(key);
                    }
                  });
                break;
              case "languages":
                query.languages = [];

                filter.checks
                  .filter(({ selected }) => selected)
                  .forEach(({ key }) => {
                    query.languages.push(key);
                  });
                break;
              case "prices":
                query.price = {};

                if (filter.value.length) {
                  let min = +filter.value?.[0].toString().replace(/\D/g, "");
                  let max = +filter.value?.[1].toString().replace(/\D/g, "");

                  if (max < min) {
                    max = min;
                    filter.value[1] = min;
                  }

                  query.price.from = min || filter.min;
                  query.price.to = max || filter.max;
                }
                break;
              case "duration":
                query.duration = {};

                if (filter.value.length) {
                  let min = Number(filter.value?.[0]);
                  let max = Number(filter.value?.[1]);

                  if (max < min) {
                    max = min;
                    filter.value[1] = min;
                  }

                  if (!this.isOneDayToursPage && max === 1) {
                    min = Number(filter.min);
                    max = Number(filter.max);

                    this.clientFilters.duration.value = [min, max];

                  } else if (this.isOneDayToursPage && min !== 1 && max !== 1) {
                    min = 1;
                    max = 1;
                  }

                  query.duration.from = min || filter.min;
                  query.duration.to = max || filter.max;
                }
                break;
              case "ages": {
                if (!query.age) {
                  query.age = {};
                }

                if (filter.value) {
                  const min = filter.value.min || filter.min;
                  const max = filter.value.max;
                  if(max === null) {
                    query.age.custom = {
                      from: {},
                      to: {}
                    }
                    query.age.custom.from.lte = min;
                    query.age.custom.to.gte = min;
                  } else {
                    query.age.from = min;

                    if (max > min) {
                      query.age.to = max;
                    }
                  }
                }
                break;
              }
              case "children": {
                if (!query.age) {
                  query.age = {};
                }

                const param = filter.value?.id?.toString();

                if (param) {
                  query.age.from = param;
                } else if (!isNaN(parseInt(filter?.input?.value))) {
                  query.age.from = filter.input.value;
                }
                break;
              }
              case "ratings":
                if (filter.value) {
                  query.rating = {
                    from: +filter.value.min,
                    to: +filter.value.max,
                  };
                }
                break;
              case "discount":
                if (filter.value) {
                  query.have_discount = 1;
                }
                break;
              case "guarantees":
                if (filter.value) {
                  query.have_guarantee = 1;
                }
                break;
              case "only_private":
                if (filter.value) {
                  query.only_private = 1;
                }
                break;
              case "have_cashback":
                if (filter.value) {
                  query.have_cashback = 1;
                }
                break;
              case "have_installment":
                if (filter.value) {
                  query.have_installment = 1;
                }
                break;
            }
          }
        });
      }

      return stringifyQuery(query);
    },
    changedFilters() {
      const changed = [];
      this.dates && changed.push("dates");
      this.location && changed.push("location");
      return Object.entries(this.clientFilters).reduce((acc, [key, value]) => {
        switch (key) {
          case "duration":
          case "prices": {
            if (
              +value.value[0]?.toString().replace(/\D/g, "") !== value.min ||
              +value.value[1]?.toString().replace(/\D/g, "") !== value.max
            ) {

              if (key === 'duration') {
                this.changeDuration(value.value);
              }
              acc.push(key);
            }
            break;
          }
          case "comforts":
          case "activities":
          case "types":
          case "languages": {
            const selected = value.checks.find(({ selected }) => selected);
            if (selected) {
              acc.push(key);
            }
            break;
          }
          case "ages":
          case "ratings":
            if (value?.value?.min || !isNaN(parseInt(value?.input?.value))) {
              acc.push(key);
            }
            break;
          case "children":
            if (value?.value?.id?.toString() || value?.value?.id === null) {
              acc.push(key);
            }
            break;
          case "discount":
          case "only_private":
          case "guarantees":
          case "have_cashback":
          case "have_installment": {
            if (value.value) {
              acc.push(key);
            }
            break;
          }
          case "spaces":
            if (value.value > 1) {
              acc.push(key);
            }
            break;
          case "group_size":
            if (value.value >= value.min && +value.value < value.max) {
              acc.push(key);
            }
            break;
        }
        return acc;
      }, changed);
    },
    changedFiltersFormatted() {
      return this.changedFilters.reduce((filters, code) => {
        const filter = this.clientFilters[code] || {};
        const { type, value, min, max, checks } = filter;
        let { label } = filter;

        /* eslint-disable no-case-declarations */
        switch (code) {
          case "location":
            label = this.location[0].label;
            break;
          case "dates":
            const [[from, to]] = this.dates;

            label = [
              `${format(from, "dd.MM.yyyy")}`,
              `${format(to, "dd.MM.yyyy")}`,
            ].join(" - ");

            break;
          default:
            break;
        }

        switch (type) {
          case "range":
            const hasMinSet =
              min?.toString() !== value[0]?.toString().replace(/\D/g, "");
            const hasMaxSet =
              max?.toString() !== value[1]?.toString().replace(/\D/g, "");

            label = hasMinSet
              ? `${this.$t("serp.filters.from")} ${value[0]} `
              : "";
            label += hasMaxSet
              ? `${
                  hasMinSet
                    ? this.$t("serp.filters.to").toLowerCase()
                    : this.$t("serp.filters.to")
                } ${value[1]} `
              : "";
            label +=
              code === "duration"
                ? this.$tc(
                    "serp.filters.durationDays",
                    +value[hasMaxSet ? 1 : 0]
                  )
                : "";
            break;
          case "checks":
            const checksSelected = checks
              .filter(({ selected }) => selected)
              .map(({ title }) => title);
            const checksMoreCount = checksSelected.slice(2).length;

            label = checksSelected.slice(0, 2).join(", ");
            label += checksMoreCount
              ? `, ${this.$t("serp.filters.checksMoreCount", {
                  count: checksMoreCount,
                })}`
              : "";
            break;
          case "buttons-select":
          case "select":
          case "radios":
            label += `: ${value.label}`;
            break;
          case "input":
            if (code === "spaces" || code === "group_size") {
              label = this.$tc("serp.filters.spacesCounter", +value);
            }
            break;
          default:
            break;
        }
        /* eslint-enable no-case-declarations */

        return [...filters, { code, label }];
      }, []);
    },
    getInstallmentPrices() {
      const pricesValue = this.clientFilters.prices.value;
      return {
        isFromOutOfRange:
          +pricesValue[0].toString().replace(/\D/g, "") < 3000 ||
          +pricesValue[0].toString().replace(/\D/g, "") > 250000,
        isToOutOfRange:
          +pricesValue[1].toString().replace(/\D/g, "") < 3000 ||
          +pricesValue[1].toString().replace(/\D/g, "") > 250000,
      };
    },
    isRuLanguage() {
      return i18n.locale === "ru";
    },
  },
  watch: {
    location: {
      handler() {
        this.setPathname();
      },
      deep: true,
    },
    productType: {
      handler() {
        if (this.productType === 'oneDay') {
          this.clientFilters.duration.value = [1, 1];
        }
      },
      deep: true,
    },
    changedFiltersFormatted: {
      handler(filters) {
        this.$emit("changed-filters", filters);
      },
      immediate: true,
    },
    "clientFilters.spaces.value": {
      handler(value, prevValue) {
        if (+value >= 3 && prevValue < 3) {
          this.popups.individual = true;
        }
      },
    },
    "clientFilters.types.checks": {
      handler() {
        this.setPathname();
      },
      deep: true,
    },
    "clientFilters.ages.value": {
      handler(value) {
        if (value) {
          this.clientFilters.children.value = null;
        }
      },
      deep: true,
    },
    "clientFilters.children.value.id": {
      handler(id) {
        if (id?.toString() || id === null) {
          this.clientFilters.ages.value = null;
        }
      },
    },
    "clientFilters.have_installment.value": {
      handler(value) {
        if (value) {
          this.setPriceForInstallment();
        }
      },
      immediate: true,
    },
    "clientFilters.duration.value": {
      handler(value) {
        if (value) {
          this.setRangeValue("duration", value);
        }
      },
      immediate: true,
    },
    "clientFilters.prices.value": {
      handler(value) {
        if (value) {
          this.setRangeValue("prices", value);
        }
      },
      immediate: true,
    },
    changedFilters: {
      handler(newValue, oldValue) {
        if (!this.arrayEquals(newValue, oldValue)) {
          const checks = ["comforts", "activities", "types", "languages"];

          // left
          let leftDifference = newValue?.filter(
            (x) => !oldValue?.includes(x)
          )[0];

          const isLeftCheck = checks.includes(leftDifference);

          if (!isLeftCheck) {
            this.initCheckFilterId = null;
          } else if (isLeftCheck) {
            this.initCheckFilterId = leftDifference;
          }

          // right
          let rightDifference = oldValue?.filter(
            (x) => !newValue?.includes(x)
          )[0];

          const isRightCheck = checks.includes(rightDifference);

          if (!isRightCheck || newValue.length <= 1) {
            this.rightCheck = "";
          } else if (isRightCheck) {
            this.rightCheck = rightDifference;
          }
        }
      },
      immediate: true,
    },
    sortQuery: {
      handler() {
        this.setQueryParams(true);
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations({
      setLoadingTours: "serp/setLoadingTours",
      setShowReset: "serp/setShowReset",
      selectDates: "serp/selectDates",
      setStrictPeriod: "serp/setStrictPeriod",
      setIsOnlyExclusive: "serp/setIsOnlyExclusive",
      setProductType: "serp/setProductType",
    }),
    ...mapActions({
      selectLocation: "serp/selectLocation",
    }),
    ...mapActions("headerSettings", ["setBackgroundImage"]),
    // checkboxes modal
    showModal({ filterId }) {
      this.modalFilterId = filterId;
      this.isModal = true;
    },
    triggerRange(filter) {
      this.clientFilters[filter].value =
        filter === "prices" ? [...this.pricesValue] : [...this.durationValue];
    },
    isHiddenFilter(filterId) {
      return this.isOneDayToursPage &&
        (filterId === "comforts" ||
          filterId === "only_private");
    },
    setRangeValue(filter, value) {
      if (filter === "prices") {

        let valueFrom = this.getPriceValue(value[0]);
        let valueTo = this.getPriceValue(value[1]);

        if (parseInt(valueTo.replace(/\D/g, "")) < parseInt(valueFrom.replace(/\D/g, ""))) {
          valueTo = valueFrom;
        }

        this.pricesValue = [
          valueFrom,
          valueTo,
        ];
      } else {
        let valueFrom = value[0];
        let valueTo = parseInt(value[1]) < parseInt(valueFrom) ? valueFrom : value[1];
        this.durationValue = [valueFrom, valueTo];
      }

      if (filter === "duration") {
          this.changeDuration(value);
      }
    },
    changeDuration(value) {

      let from = Number(value[0]);
      let to = Number(value[1]);

      if ((from !== 1 || to !== 1) && this.isOneDayToursPage) {

        this.setProductType('multiDays');

      } else if (from === 1 && to === 1) {
        this.setProductType('oneDay');
      }
    },
    setInputValue: debounce(function (filter, value, key) {
      if (filter === "prices") {
        this.pricesValue[key] = this.getPriceValue(value);
      } else {
        this.durationValue[key] = +value?.toString().replace(/\D/g, "");
      }

      this.clientFilters[filter].value.splice(
        key,
        1,
        filter === "prices"
          ? this.getPriceValue(value)
          : +value?.toString().replace(/\D/g, "")
      );
    }, 500),
    getPriceValue(value) {
      return `${this.currencyChar} ${
        value
          ?.toString()
          .replace(/\D/g, "")
          .replace(/\B(?=(\d{3})+(?!\d))/g, " ") || 0
      }`;
    },
    // setting filters' check values
    setCheckValue(check) {
      if (check.disabled) {
        return;
      }

      check.selected = !check.selected;

      if (check.key === "1151" && !check.selected) {
        Cookies.set("def_lang", "0");
      }
    },
    setButtonSelect(filter, value) {
      filter?.value?.label === value.label
        ? (filter.value = null)
        : (filter.value = value);
    },
    setChildAge(value) {
      value = value.toString();
      if (!isNaN(parseInt(value)) && value.length <= 2) {
        this.clientFilters.ages.value = null;
        this.clientFilters.ages.input.value = parseInt(value);
      } else {
        this.clientFilters.ages.input.value = "";
      }
    },
    // update client filters by loaded facets
    setClientFilters({ filters, init = false }) {
      this.clientFilters = Object.entries(filters || {}).reduce(
        (acc, [key, value]) => {
          switch (key) {
            case "duration":
              {
                if (init) {

                  if (this.query.duration?.from == 1 && this.query.duration?.to == 1) {
                    this.setProductType('oneDay');
                  } else if (this.query?.only_exclusive == 1) {
                    this.setProductType('exclusive');
                  }

                  const initValue = [value.min, Math.ceil(+value.max)];
                  acc[key] = {
                    ...this.clientFilters[key],
                    value: [value.min, Math.ceil(+value.max)],
                    min: Math.ceil(+value.min),
                    max: Math.ceil(+value.max),
                  };
                  this.durationValue = initValue;
                } else {
                  const newValue =
                    this.changedFilters.includes("duration") ||
                    (!value.min && !value.max)
                      ? this.clientFilters[key].value
                      : [value.min, Math.ceil(+value.max)];
                  acc[key] = {
                    ...this.clientFilters[key],
                    value: newValue,
                    min:
                      this.changedFilters.includes("duration") ||
                      (!value.min && !value.max)
                        ? this.clientFilters[key].min
                        : Math.ceil(+value.min),
                    max:
                      this.changedFilters.includes("duration") ||
                      (!value.min && !value.max || this.isOneDayToursPage)
                        ? this.clientFilters[key].max
                        : Math.ceil(+value.max),
                  };
                  this.durationValue = newValue;
                }
              }
              break;
            case "prices":
              {
                if (init) {
                  const initValue = [
                    this.getPriceValue(Math.ceil(+value.min)),
                    this.getPriceValue(Math.ceil(+value.max)),
                  ];
                  acc[key] = {
                    ...this.clientFilters[key],
                    value: [
                      this.getPriceValue(Math.ceil(+value.min)),
                      this.getPriceValue(Math.ceil(+value.max)),
                    ],
                    min: Math.ceil(+value.min),
                    max: Math.ceil(+value.max),
                  };
                  this.pricesValue = initValue;
                } else {
                  const newValue =
                    this.changedFilters.includes("prices") ||
                    (!value.min && !value.max)
                      ? this.clientFilters[key].value
                      : [
                          this.getPriceValue(Math.ceil(+value.min)),
                          this.getPriceValue(Math.ceil(+value.max)),
                        ];
                  acc[key] = {
                    ...this.clientFilters[key],
                    value: newValue,
                    min:
                      this.changedFilters.includes("prices") ||
                      (!value.min && !value.max)
                        ? this.clientFilters[key].min
                        : Math.ceil(+value.min),
                    max:
                      this.changedFilters.includes("prices") ||
                      (!value.min && !value.max)
                        ? this.clientFilters[key].max
                        : Math.ceil(+value.max),
                  };
                  this.pricesValue = newValue;
                }
              }
              break;
            case "comforts":
            case "activities":
            case "types":
            case "languages": {
              let checks = [];
              if (init) {
                if (key === "comforts" || key === "activities") {
                  checks = this.clientFilters[key].checks.map(
                    (clientCheck) => ({
                      ...clientCheck,
                      selected: false,
                      disabled: false,
                    })
                  );
                } else {
                  const keyLang = this.isRuLanguage ? "1151" : "1152";
                  checks = value
                    .map((clientCheck) => ({
                      ...clientCheck,
                      selected: false,
                      disabled: false,
                    }))
                    .sort((a, b) => {
                      if (a.key === b.key) return 0;
                      if (a.key === keyLang) return -1;
                      if (b.key === keyLang) return 1;

                      if (a.title < b.title) {
                        return -1;
                      }
                      if (a.title > b.title) {
                        return 1;
                      }
                      return 0;
                    });
                }
              } else {
                const filteredChecks = this.clientFilters[key].checks.map(
                  (clientCheck) => {
                    return {
                      ...clientCheck,
                      disabled: clientCheck.selected
                        ? false
                        : this.initCheckFilterId === key
                        ? clientCheck.disabled
                        : !value.filter((check) => {
                            if (Array.isArray(clientCheck.key)) {
                              return clientCheck.key.includes(check.key);
                            } else {
                              return check.key === clientCheck.key;
                            }
                          }).length,
                    };
                  }
                );

                if (this.rightCheck && this.rightCheck === key) {
                  checks = filteredChecks;
                } else if (this.rightCheck && this.rightCheck !== key) {
                  checks =
                    this.checks[key].length && this.checks[key].length === 2
                      ? this.checks[key][1]
                      : this.checks[key].length === 1
                      ? this.checks[key][1]
                      : filteredChecks;
                } else if (!this.rightCheck) {
                  checks = filteredChecks;
                }

                if (checks.length > 5) {
                  checks = checks
                    .sort((a, b) => b.selected - a.selected)
                    .sort((a, b) => a.disabled - b.disabled);
                }

                if (this.checks[key].length >= 2) {
                  this.checks[key].pop();
                }
                this.checks[key].unshift([...checks]);
              }

              acc[key] = {
                ...this.clientFilters[key],
                checks,
              };
              break;
            }
            case "discount":
            case "only_private":
            case "guarantees":
              acc[key] = {
                ...this.clientFilters[key],
              };
              break;
            case "ages":
            case "spaces":
            case "ratings":
              acc[key] = {
                ...this.clientFilters[key],
                ...value,
              };
              break;
            case "group_size":
              if (this.clientFilters[key].value === 0) {
                acc[key] = {
                  ...this.clientFilters[key],
                  value: Math.ceil(+value.max),
                  min: Math.ceil(+value.min),
                  max: Math.ceil(+value.max)
                };

                this.clientFilters[key].value = Math.ceil(+value.max);

                break
              }

              acc[key] = {
                ...this.clientFilters[key],
                ...value,
                min: this.clientFilters[key].min,
                max: this.clientFilters[key].max
              };

              break;
          }

          return acc;
        },
        this.clientFilters
      );

      this.$emit("setParticipants", this.clientFilters.spaces.value);

      if (init) {
        this.initFilters = JSON.parse(JSON.stringify(this.clientFilters));
        this.firstPageLoad = true;
      }
    },
    // reset client filters to init state
    resetFilters() {
      const { stringifyQuery } = useQueryString();

      const query = stringifyQuery(this.sortQuery);
      const path = `${fakeWindow.location.pathname}?${query}`;

      this.rightCheck = "";
      this.checks = {
        comforts: [],
        activities: [],
        types: [],
        languages: [],
      };
      this.clientFilters = JSON.parse(JSON.stringify(this.initFilters));
      this.pushStateToHistory({}, document.title, path);
      this.hiddenFilters = "";
      this.selectDates(null);
      this.selectLocation(null);
      this.setShowReset(false);
    },
    resetFilter(type) {
      this.checks[type] = [];
      this.clientFilters[type] = {
        ...this.initFilters[type],
        visible: this.clientFilters[type].visible,
      };
    },
    refresh() {
      if (this.$refs.rangeduration) {
        this.$refs.rangeduration[0]?.refresh();
      }

      this.$refs.rangeprices[0]?.refresh();
    },
    readQueryParams() {
      let queryFilters = { ...this.clientFilters };
      this.hiddenFilters = {};
      Object.entries(this.query).forEach(([key, value]) => {
        switch (key) {
          case "period": {
            const periods = Object.values(value).map(({ from, to, month }) => {
              if (month) {
                return [
                  startOfMonth(parse(month, "dd.MM.yyyy", new Date())),
                  endOfMonth(parse(month, "dd.MM.yyyy", new Date())),
                  "month",
                ];
              }

              return [
                parse(from, "dd.MM.yyyy", new Date()),
                parse(to, "dd.MM.yyyy", new Date()),
              ];
            });

            this.selectDates(periods);
            this.setShowReset(true);
            break;
          }
          case "loc": {
            this.setShowReset(true);
            break;
          }
          case "activity": {
            queryFilters.activities.checks = queryFilters.activities.checks.map(
              (check) => {
                return {
                  ...check,
                  selected: value.includes(check.key),
                };
              }
            );
            queryFilters.activities.visible = true;
            this.setShowReset(true);
            break;
          }
          case "types": {
            queryFilters.types.checks = queryFilters.types.checks.map(
              (check) => {
                return {
                  ...check,
                  selected: value.includes(check.key),
                };
              }
            );
            queryFilters.types.visible = true;
            this.setShowReset(true);
            break;
          }
          case "age": {
            if (+value?.from >= 18 && value?.to) {
              const foundBtn = queryFilters.ages.buttons.find(
                ({ min }) => min === +value.from
              );

              queryFilters.ages.value = foundBtn;
              queryFilters.ages.visible = true;
            } else if(+value?.custom?.from?.lte > 18) {
              const foundBtn = queryFilters.ages.buttons.find(
                ({ min }) => min === +value?.custom?.from?.lte
              );

              queryFilters.ages.value = foundBtn;
              queryFilters.ages.visible = true;
            }

            if (+value?.from < 18 && !value?.to) {
              queryFilters.children.value = queryFilters.children.options.find(
                ({ id }) => id?.toString() === value.from.toString()
              );
              queryFilters.children.visible = true;
            }

            this.setShowReset(true);
            break;
          }
          case "available_spaces": {
            queryFilters.spaces.value = value;
            this.setShowReset(true);
            break;
          }
          case "group_size": {
            queryFilters.group_size.value = value;
            this.setShowReset(true);
            break;
          }
          case "comfort": {
            queryFilters.comforts.checks = queryFilters.comforts.checks.map(
              (check) => {
                if (Array.isArray(check.key)) {
                  return {
                    ...check,
                    selected: check.key.some((r) => value.includes(r)),
                  };
                }
                return {
                  ...check,
                  selected: value.includes(check.key),
                };
              }
            );
            queryFilters.comforts.visible = true;
            this.setShowReset(true);
            break;
          }
          case "duration": {
            if (value?.from) {
              queryFilters.duration.value[0] = value.from;
              this.durationValue[0] = value.from;
            }
            if (value?.to) {
              queryFilters.duration.value[1] = value.to;
              this.durationValue[1] = value.to;
            }
            this.setShowReset(true);
            break;
          }
          case "price": {
            if (value?.from) {
              queryFilters.prices.value[0] = this.getPriceValue(value.from);
              this.pricesValue[0] = this.getPriceValue(value.from);
            }
            if (value?.to) {
              queryFilters.prices.value[1] = this.getPriceValue(
                Math.ceil(+value.to)
              );
              this.pricesValue[1] = this.getPriceValue(Math.ceil(+value.to));
            }
            this.setShowReset(true);
            break;
          }
          case "have_discount": {
            queryFilters.discount.value = !!value;
            this.setShowReset(true);
            break;
          }
          case "have_guarantee": {
            queryFilters.guarantees.value = !!value;
            this.setShowReset(true);
            break;
          }
          case "only_private": {
            queryFilters.only_private.value = !!value;
            this.setShowReset(true);
            break;
          }
          case "is_period_strict": {
            this.setStrictPeriod(!!+value);
            break;
          }
          case "lang": {
            let isCheckItem = !!queryFilters.languages.checks.find(check => check.selected === true);

            if (isCheckItem === false) {
              const isRu = value === "ru" && Cookies.get("def_lang") === "ru";
              const isEn = value === "en";

              queryFilters.languages.checks = queryFilters.languages.checks.map(
                (check) => {
                  return {
                    ...check,
                    selected: isRu
                      ? check.key === "1151"
                      : isEn
                        ? check.key === "1152"
                        : false,
                  };
                }
              );
            }

            queryFilters.languages.visible = true;
            this.setShowReset(true);
            break;
          }
          case "languages": {
            queryFilters.languages.checks = queryFilters.languages.checks.map(
              (check) => {
                return {
                  ...check,
                  selected: value.includes(check.key),
                };
              }
            );
            queryFilters.languages.visible = true;
            this.setShowReset(true);
            break;
          }
          case "rating": {
            if (value.from) {
              const foundRadio = queryFilters.ratings.radios.find((radio) => {
                return +radio.min === +value.from;
              });

              queryFilters.ratings.value = foundRadio;
              queryFilters.ratings.visible = true;
            }
            this.setShowReset(true);
            break;
          }
          case "have_cashback": {
            queryFilters.have_cashback.value = !!value;
            this.setShowReset(true);
            break;
          }
          case "have_installment": {
            queryFilters.have_installment.value = !!value;
            this.setShowReset(true);
            break;
          }
          case "only_exclusive": {
            this.setIsOnlyExclusive(!!+value);
            break;
          }
          case "excluded_tours":
          case "experts":
          case "ids":
          case "master_iata":
          case "have_early_booking":
          case "only_weekend":
          case "only_en":
            this.hiddenFilters[key] = value;
            break;
        }
      });

      this.clientFilters = { ...queryFilters };
      //this.hiddenFilters = Object.entries(this.query).reduce(
      //  (acc, [key, value]) => {
      //    switch (key) {
      //      case "excluded_tours":
      //      case "experts":
      //      case "ids":
      //      case "master_iata":
      //        acc =
      //          acc +
      //          value.reduce((query, el) => query + `${key}[]=${el}&`, "");
      //        break;
      //      case "have_early_booking":
      //      case "only_weekend":
      //      case "only_en":
      //        acc = acc + `${key}=${value}&`;
      //        break;
      //    }
      //    return acc;
      //  },
      //  ""
      //);

      if (
        this.query.period ||
        this.query.loc ||
        this.changedFilters.length ||
        this.hiddenFilters
      ) {
        this.getFacets();
      } else {
        this.onFacetsLoaded();
      }
    },
    onFacetsLoaded() {
      const { parseString } = useQueryString();

      this.$emit("loadedFacets", parseString(this.filtersQuery));
    },
    setQueryParams(sort = false) {

      let pathType = '';

      if (this.isOneDayToursPage) {
        pathType = 'days-1';
      } else if (this.isExclusiveToursPage) {
        pathType = 'exclusive';
      }

      if (
        !this.changedFilters.length &&
        !this.location &&
        !this.dates &&
        !this.hiddenFilters &&
        !sort
      ) {
        const pathname = '/tours/' + pathType;
        this.getPageMetaData( pathname);
        this.pushStateToHistory({}, document.title, pathname);
        this.setShowReset(false);
      } else {

        let pathname = fakeWindow.location.pathname;
        const segments = pathname.split("/");
        const lastSegment = decodeURIComponent(segments[segments.length - 1]);

        //если локация, для которой нет сео страницы
        let notNeedChangeUrl = this.pathname == "location" && (pathname == '/tours/' || pathname == '/tours');

        const selectedType = this.clientFilters.types.checks.filter(
          ({ selected }) => selected
        );

        const isSpecial = pathname.indexOf("/specials/") !== -1;

        if (!isSpecial) {
          switch (this.pathname) {
            case "country":
              pathname = `/tours/${this.location[0].type}/${this.location[0].code}`;
              break;
            case "type":
              pathname = `/tours/type/${selectedType[0].code}`;
              break;
            case "location":
              pathname = `/tours/${this.location[0].type}/${this.location[0].code}`;
              break;
            default:
              pathname = `/tours/`;
          }
        }

        const basePath = pathname;

        if (pathType == ''
          && this.location !== null
          && this.location[0].code !== lastSegment
          && pathname.indexOf(lastSegment) == -1
          && lastSegment !== "days-1"
          && lastSegment !== "exclusive") {
          pathname = pathname + `/${lastSegment}`;
        }

        let defaultPathname = document.querySelector('.js-page-header-img').getAttribute('data-page');

        if (!pathType && defaultPathname.indexOf(pathType) >= 0) {
          defaultPathname = defaultPathname.replace(new RegExp('/days-1|/exclusive', 'g'), '');

        } else if (pathType && defaultPathname.indexOf(pathType) === -1) {

          defaultPathname = defaultPathname.replace(new RegExp('/days-1|/exclusive', 'g'), '');
          defaultPathname = defaultPathname + `/${pathType}`;
        }

        if (pathType) {
          pathname += (pathname === '/tours/') ? `${pathType}` : `/${pathType}`;
        }

        if (pathname !== fakeWindow.location.pathname) {

          if (pathType == ''
            && this.location !== null
            && this.location[0].code !== lastSegment
            && pathname.indexOf(lastSegment) == -1
            && lastSegment !== "days-1"
            && lastSegment !== "exclusive") {
            pathname = pathname + `/${lastSegment}`;
          }

          // обновляем мета теги и меняем url при необходимости
          if (this.pathname == "location") {
            let locationPath = notNeedChangeUrl? '/tours/' : basePath;
            this.getPageMetaData(pathname, basePath, true, locationPath);
          } else {
            this.getPageMetaData(pathname, basePath);
          }

        } else if (!this.emptyQueryParams() || !this.firstPageLoad) {
          this.updateUrlByPath(pathname);
        }

        !sort && this.setShowReset(true);
      }
    },
    emptyQueryParams() {
      let emptyQueryParams = this.firstPageLoad
        && fakeWindow.location.search === ""
        && fakeWindow.location.pathname !== "/tours/"
        || fakeWindow.location.search === "?";
      this.firstPageLoad = false;
      return emptyQueryParams;
    },
    setPathname() {
      if (
        this.location?.length === 1 &&
        this.location[0].type !== "location" &&
        this.clientFilters.types.checks.filter(({ selected }) => selected)
          .length !== 1
      ) {
        this.pathname = "country";
      } else if (
        (!this.location || this.location?.length !== 1) &&
        this.clientFilters.types.checks.filter(({ selected }) => selected)
          .length === 1
      ) {
        this.pathname = "type";
      } else if ( this.location?.length === 1 && this.location[0].type === "location") {
        this.pathname = "location";
      } else if (
        (!this.location || this.location?.length !== 1) &&
        this.clientFilters.types.checks.filter(({ selected }) => selected)
          .length !== 1
      ) {
        this.pathname = "";
      }
    },
    setMetaData(metaData) {
      const {
        breadcrumb,
        canonical_url,
        meta_description,
        meta_title,
        serp_footer_seo_text,
        serp_header_seo_text,
        title,
        tours_by_month,
        tours_by_routes,
        tours_by_seo_categories,
        tours_by_auto_categories,
        photo_background,
        individual_seo_tags,
      } = metaData;

      const toursBody = document.getElementsByClassName("tours-body")[1];


      // canonical_url
      document
        .querySelector('link[rel="canonical"]')
        .setAttribute("href", canonical_url);

      // meta
      document
        .querySelector('meta[name="description"]')
        .setAttribute("content", meta_description);

      document
        .querySelector('meta[property="og:description"]')
        .setAttribute("content", meta_description);

      document.querySelector("title").textContent = meta_title;

      // breadcrumb
      const breadcrumbsEl =
        document.getElementsByClassName("breadcrumbs")[0].children;

      while (breadcrumbsEl.length !== breadcrumb.length) {
        if (breadcrumbsEl.length > breadcrumb.length) {
          breadcrumbsEl[0].remove();
        } else if (breadcrumbsEl.length < breadcrumb.length) {
          const clonedNode = breadcrumbsEl[0].cloneNode(true);
          document.getElementsByClassName("breadcrumbs")[0].prepend(clonedNode);
        }
      }

      let counter = 1;
      for (let i = breadcrumb.length - 1; i >= 0; i--) {
        const el = breadcrumbsEl[breadcrumbsEl.length - counter];
        if (el.tagName === "DIV") {
          el.children[0].textContent = breadcrumb[i].title;
        } else {
          el.children[0].children[0].textContent = breadcrumb[i].title;
          el.children[0].href = breadcrumb[i].link;
        }
        counter++;
      }

      if(this.location?.length === 1
        && this.location[0].id === 695
        && this.location[0].type === "region") {
        // tours_by_month
        let tolstoy = document.getElementsByClassName("js-tolstoy");
        if (tolstoy.length) {
          var n = document.createElement('div')
          n.setAttribute("data-publish-id", "mq2mjn2u1vs7j")
          n.setAttribute("class", "tolstoy-stories");
          tolstoy[0].append(n);
        }
      } else {
        let tolstoy = document.getElementsByClassName("tolstoy-stories");
        if (tolstoy.length) {
          tolstoy[0].remove();
        }
      }
      // title
      document.getElementsByClassName("header-h1")[0].children[0].textContent =
        title;

      // tours_by_month
      const toursByMonth = document.getElementsByClassName("tours-by-month");

      if (toursByMonth.length) {
        toursByMonth[0].remove();
      }

      if (tours_by_month?.length) {
        toursBody.appendChild(
          this.setToursTags(
            tours_by_month,
            "tours-month-list--month",
            "show-more-month",
            this.$t("serp.seo.toursByMonth"),
            "tours-by-month"
          )
        );
      }


      //individual_seo_tags
      const toursIndividualSeoTags = document.getElementsByClassName("tours-individual-seo-tags");

      while (toursIndividualSeoTags.length) {
        toursIndividualSeoTags[0].remove();
      }
      let elementName = '';
      let elementLinks = [];

      for (var individual_key in individual_seo_tags) {
        let element = individual_seo_tags[individual_key];
        if (individual_key == 'name') {
          elementName = element;
        }
        if (individual_key == 'collections') {
          elementLinks = element;
        }
      }

      if (elementLinks?.length) {
        toursBody.appendChild(
          this.setToursTags(
            elementLinks,
            "tours-list--individual-seo-tags",
            "show-more-individual-seo-tags",
            elementName,
            "tours-individual-seo-tags"
          )
        );
      }
      //individual_seo_tags (end)

      // tours_by_routes
      const toursByRoutes = document.getElementsByClassName("tours-by-routes");

      while (toursByRoutes.length) {
        toursByRoutes[0].remove();
      }

      if (tours_by_routes?.length) {
        toursBody.appendChild(
          this.setToursTags(
            tours_by_routes,
            "tours-month-list--routes",
            "show-more-routes",
            this.$t("serp.seo.toursByRoutes"),
            "tours-by-routes"
          )
        );
      }

      //tours_by_auto_categories
      const toursByAutoCategory = document.getElementsByClassName("tours-by-auto-categories");

      while (toursByAutoCategory.length) {
        toursByAutoCategory[0].remove();
      }

      for (var key in tours_by_auto_categories) {

        let element = tours_by_auto_categories[key];

        if (!element.LINKS?.length || !element.CATEGORY?.NAME) {
          continue;
        }

        toursBody.appendChild(
          this.setToursTags(
            element.LINKS,
            "tours-list--auto-categories",
            "show-more-auto-categories",
            element.CATEGORY.NAME,
            "tours-by-auto-categories"
          )
        );
      }

      //serp_header_seo_text
      const seoTextHeader = document.getElementsByClassName("seo-uppercontent");
      if (seoTextHeader.length) {
        seoTextHeader[0].remove();
      }
      if (serp_header_seo_text) {
        const sectionSeoHeader = document.createElement("section");
        const divH1 = document.getElementsByClassName("header-h1 ");

        sectionSeoHeader.classList.add("seo-uppercontent", "content", "seo-text-content", "show" );
        sectionSeoHeader.innerHTML = serp_header_seo_text;
        divH1[0].insertAdjacentElement("afterend", sectionSeoHeader);
      }


      for (var categoryId in tours_by_seo_categories) {
        let categoryData = tours_by_seo_categories[categoryId];
        if (!categoryData.ITEMS?.length) {
          continue;
        }

        toursBody.appendChild(
          this.setToursTags(
            categoryData.ITEMS,
            "tours-month-list--routes",
            "show-more-routes",
            categoryData.CATEGORY_NAME,
            "tours-by-routes"
          )
        );
      }

      // serp-footer-seo-text
      const seoText = document.getElementsByClassName("seo-section");

      if (seoText.length) {
        seoText[0].remove();
      }

      if (serp_footer_seo_text) {
        const div = document.createElement("div");
        const readMore = document.createElement("div");
        const gradient = document.createElement("div");
        const section = document.createElement("section");
        const seoWrapper = document.createElement("div");

        gradient.classList.add("seo-section-gradient");

        seoWrapper.classList.add("seo-content-wrapper");
        seoWrapper.innerHTML = serp_footer_seo_text;
        seoWrapper.appendChild(gradient);

        readMore.classList.add("seo-section__read-more");
        readMore.innerHTML = this.$t("serp.seo.readMore");

        section.classList.add("content");
        section.appendChild(seoWrapper);
        section.appendChild(readMore);

        div.classList.add("seo-section");
        div.appendChild(section);

        toursBody.appendChild(div);
      }

      fakeWindow.applySeoTextTranforms();

      const photoBackgroundEl = document.querySelector('.js-page-header-img');
      if (photoBackgroundEl) {
        photoBackgroundEl.classList.toggle('serp-photo-background', photo_background);
        photoBackgroundEl.innerHTML = photo_background ?? "";
        this.updatePhotoBackgroundHeight();
      }
    },
    setToursTags(arr, listClass, btnClass, headerText, divClass) {
      const div = document.createElement("div");
      const divInner = document.createElement("div");
      const h2 = document.createElement("h2");
      const list = document.createElement("div");
      const showMore = document.createElement("div");
      let showMoreButton = false;

      list.classList.add("js-show-more-container", "tours-month-list", listClass);
      for (let i = 0; i < arr.length; i++) {
        const a = document.createElement("a");

        if (i > 6) {
          a.classList.add("tours-month-item--hidden");
          showMoreButton = true;
        }

        a.classList.add("tours-month-item", "tours-month-item--month");
        a.href = arr[i].link;
        a.innerHTML = arr[i].title;
        list.appendChild(a);
      }

      if (showMoreButton) {
        showMore.classList.add("js-show-more-btn", "tours-month__show-more", btnClass);
        showMore.innerHTML = this.$t("serp.seo.showMore");
        list.append(showMore);
      }

      h2.innerHTML = headerText;

      divInner.appendChild(h2);
      divInner.appendChild(list);

      div.classList.add("tours-month", divClass);
      div.appendChild(divInner);

      return div;
    },
    setTypeTour() {
      let pathName = vueOptions.ssr ? context.url : window.location.pathname;

      const isOneDay = pathName.indexOf("/days-1") !== -1;
      const isExclusive = pathName.indexOf("/exclusive") !== -1;

      if (!isOneDay && !isExclusive) {
        return;
      }

      if (isOneDay) {
        this.setProductType('oneDay');
      }

      if (isExclusive) {
        this.setProductType('exclusive');
      }

      //this.getPageMetaData(pathName, false, false);
    },
    setPriceForInstallment() {
      const currencyId = this.currency.id.toLowerCase();
      let pricesValue = [...this.clientFilters.prices.value];

      if (currencyId !== "rub") {
        return;
      }

      if (this.getInstallmentPrices.isFromOutOfRange) {
        pricesValue[0] = this.getPriceValue(3000);
      }

      if (this.getInstallmentPrices.isToOutOfRange) {
        pricesValue[1] = this.getPriceValue(250000);
      }

      this.pricesValue = pricesValue;
      this.clientFilters.prices.value = pricesValue;
      this.clientFilters.prices.min = parseInt(pricesValue[0].toString().replace(/\D/g, ""));
      this.clientFilters.prices.max = parseInt(pricesValue[1].toString().replace(/\D/g, ""));
    },
    // api
    async getFacets() {
      if (this.isFetching) {
        return;
      }

      this.isFetching = true;
      const { getToursFacets } = useSerp();

      this.setLoadingTours(true);

      try {
        const query = this.filtersQuery;
        const { filters } = await getToursFacets(query);

        this.setClientFilters({ filters });
      } catch (err) {
        console.log(err);
      } finally {
        // this.setLoadingTours(true);

        this.onFacetsLoaded();
        this.isFetching = false;
      }
    },
    updateUrlByPath(path)
    {
      path = path == '/tours' ? `${path}/`: path;
      const { stringifyQuery } = useQueryString();
      const query = stringifyQuery(this.sortQuery);
      let pathname = `${path}${path.includes('?') ? '&' : '?'}${this.filtersQuery}&${query}`;

      this.pushStateToHistory({
          pathname: pathname,
        },
        document.title,
        pathname);
    },
    async getPageMetaData(pathName, basePathName = false, updatePath = true, locationPath = '') {

      let pageError = false;
      const { getPageMetaData } = usePageMeta();

      try {
        const { data } = await getPageMetaData(pathName);
        this.setMetaData(data);

        if (data.tours_redirect_url) {
          pathName = data.tours_redirect_url;
        }

        if (updatePath && !this.emptyQueryParams()) {
          this.updateUrlByPath(pathName);
        }

      } catch (err) {
        pageError = err;
      }

      // Если страница была не найдена и был передан путь без постфикса
      if (pageError && basePathName && basePathName !== pathName) {
        try {
          const { data } = await getPageMetaData(basePathName);
          this.setMetaData(data);
          //и если не нужно менять url
          if(locationPath.length > 0) {
            this.updateUrlByPath(locationPath);
          }
          else {
            this.updateUrlByPath(basePathName);
          }

          pageError = false;

        } catch(err) {
          pageError = err;
        }
      }

      if (pageError) {
        this.getBackendErrorMessage(pageError);
      }
    },
    async init() {
      try {
        if (this.query?.loc?.length) {
          await this.selectLocation(this.query.loc);
        }

        await this.readQueryParams();
      } catch (err) {
        console.log(err);
      }
    },
    updatePhotoBackgroundHeight: debounce(() => fakeWindow.requestAnimationFrame(() => {
      const photoBackgroundEl = document.querySelector('.serp-photo-background');
      const offset = document.querySelector('.serp').offsetTop + document.getElementById('suggest').offsetTop;

      if (photoBackgroundEl) {
        photoBackgroundEl.style.setProperty('--serp-photo-background-height', `${offset}px`);
      }
    }), 100),
    getBackendErrorMessage,
  },
  async mounted() {
    if (Cookies.get("def_lang") !== "0" && this.query.lang === "ru") {
      Cookies.set("def_lang", "ru");
    }

    await this.init();

    if(!history.state && typeof fakeWindow !== 'undefined') {

      const queryParams = new URLSearchParams(fakeWindow.location.search);
      let urlPath = fakeWindow.location.pathname;

      if (queryParams.size !== 0) {
        urlPath = urlPath + '?' + queryParams.toString();
      }

      this.pushStateToHistory({
          pathname: urlPath,
        },
        document.title,
        urlPath);
    }

    this.$watch("filtersQuery", () => {
      this.getFacets();
      this.setQueryParams();
    });

    fakeWindow.addEventListener("popstate", (event) => {
      if(event.state) {
        fakeWindow.location.href = event.state.pathname;
      }
    });

    this.updatePhotoBackgroundHeight();
    fakeWindow.addEventListener("resize", this.updatePhotoBackgroundHeight);
  },
  beforeDestroy() {
    fakeWindow.removeEventListener("popstate");
    fakeWindow.removeEventListener("resize", this.updatePhotoBackgroundHeight);
  },
  created() {
    this.setTypeTour();

    if (!this.isRuLanguage) {
      delete this.clientFilters.have_installment;
    }

    this.setClientFilters({
      filters: this.initFacets?.filters || {},
      init: true,
    });
  },
};
</script>

<style lang="scss" scoped>
.filters {
  width: 100%;
  background: var.$white;

  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  overflow: auto;

  @include mixin.size-md {
    z-index: 89000;
  }

  &--modal {
    z-index: 89002;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px 18px;
    border-bottom: 1px solid var.$gray-light;

    &__back {
      position: absolute;
      left: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 100px;
      width: 28px;
      height: 28px;
      background: var.$gray-light;
      cursor: pointer;
    }

    &__text {
      color: var.$black;
    }
  }

  &__item {
    &:after {
      display: block;
      clear: both;
      content: "";
      position: relative;
      left: 0;
      bottom: 0;
      width: 90%;
      border-bottom: 1px solid var.$gray-light;
      margin: 0 auto;
    }

    &._popup-highlight {
      @include mixin.decor();
      position: relative;
      z-index: 999999;
      background: var.$white;
      border-radius: var.$rounded-8;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      & > div {
        line-height: 18px;
      }
    }

    &__reset {
      color: var.$green-dark;
      cursor: pointer;
    }

    &__helper {
      position: relative;

      &-text {
        color: var.$secondary;
        margin: 8px 8px 8px 0;
      }

      &-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;

        &:hover {
          & + .filters__item__helper-modal {
            display: block;
          }
        }
      }

      &-modal {
        position: absolute;
        background: #121212;
        border-radius: 12px;
        padding: 12px 16px;
        color: var.$white;
        display: none;
        z-index: 1;
        width: 200px;
        text-align: center;
        left: 30px;
        bottom: -20px;

        &:before {
          content: "";
          position: absolute;
          left: -4px;
          top: 22px;
          width: 0;
          height: 0;
          border-top: 9px solid transparent;
          border-right: 9px solid #121212;
          transform: rotate(135deg);
        }
      }
    }

    &__dropdown {
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
      height: 40px;
      padding: 0 16px;

      & > div:first-child {
        line-height: 18px;
      }

      &__reset {
        display: flex;
        align-items: center;
        justify-content: space-between;
        grid-gap: 12px;
      }

      &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 200ms;

        &--active {
          transform: rotate(180deg);
        }
      }

      &__menu {
        visibility: hidden;
        max-height: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        transition: all 200ms;
        padding: 0 16px;

        &--visible {
          visibility: visible;
          max-height: 300px;
          padding-bottom: 23.5px;
          overflow: visible;
        }

        &--visible-range {
          visibility: visible;
          max-height: 300px;
          padding-bottom: 12px;
        }
      }

      &.wrapped:hover {
        background: #f6f7fa;
      }
    }

    &__range {
      transition: all 200ms;

      &__inputs {
        display: flex;
        margin: 0 0 10px 0;

        & > div {
          width: 100%;
        }

        &__left >>> .input-wrapper {
          border-radius: 8px 0 0 8px;
          border-right-width: 0.5px;

          &:focus-within:not(.input-wrapper--disabled):not(.input-wrapper--invalid) {
            border: 1px solid var.$black;
            border-right-width: 0.5px;
          }
        }

        &__right {
          & >>> .input-wrapper {
            border-radius: 0 8px 8px 0;
            border-left-width: 0.5px;

            &:focus-within:not(.input-wrapper--disabled):not(.input-wrapper--invalid) {
              border: 1px solid var.$black;
              border-left-width: 0.5px;
            }
          }
        }
      }

      &.highlight {
        background: #eeeef2;

        & >>> input {
          background: #eeeef2;
        }
      }
    }

    &__checks {
      &__item {
        display: flex;
        align-items: center;
        grid-gap: 10px;
        margin-bottom: 16px;
        cursor: pointer;

        &.disabled {
          cursor: default;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }

      &__button {
        margin-top: 10px;
        border-radius: 8px !important;
      }
    }

    &__switch {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 16px;
      height: 40px;
      cursor: pointer;

      &__label {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding-right: 12px;
      }

      &:hover {
        background: #f6f7fa;

        & >>> .switch:not(.checked) {
          & > div {
            box-shadow: 7.5px 1.5px 22.5px rgba(100, 100, 119, 0.4);
          }
        }

        & >>> .checked {
          background: #9aae2d;
        }
      }
    }

    &__buttons-select {
      &__buttons {
        display: grid;
        grid-template-columns: repeat(3, 0.33fr);
        grid-gap: 8px;

        & > div {
          padding: 11px 0;
        }
      }

      &__input {
        margin-top: 8px;
      }
    }

    &__input {
      display: flex;
      flex-direction: column;

      @media (min-width: var.$size-md) {
        & >>> .input-label {
          font-size: 10px;
        }
      }
    }

    &__radios {
      &__item {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        cursor: pointer;
      }

      &__radio {
        margin-right: 10px;
      }

      &__rating {
        background: var.$gray-light;
        border-radius: 4px;
        padding: 4px 6.58px;
        margin-right: 8px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__button {
    margin: 16px;
  }

  &-modal {
    &-wrapper {
      width: 100%;
    }

    &__header {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      color: var.$black;
      border-bottom: 1px solid var.$gray-light;

      &__icon {
        position: absolute;
        right: 24px;
        top: 24px;
        cursor: pointer;
      }
    }

    &__body {
      display: grid;
      grid-template-columns: 1fr;
      max-height: calc(100vh - 90px);
      padding: 24px;
      overflow-y: auto;
      overflow-x: hidden;

      &::-webkit-scrollbar {
        display: none;
      }

      @media (min-width: var.$size-sm) {
        max-height: calc(100vh - 200px);
        grid-template-columns: repeat(2, 1fr);
      }

      @media (min-width: var.$size-md) {
        grid-template-columns: repeat(3, 0.33fr);
      }
    }
  }
}

@media (min-width: var.$size-lg) {
  .filters {
    position: relative;
    height: 100%;
    border-radius: 16px;
    overflow: unset;

    &__header {
      display: none;
    }
  }
}

.switch-filter__uniq-serp-class {
  margin-right: 10px;
  padding: 12.5px 0px;
}

.filters-uniq-serp__item__switch {
  height: auto !important;
}
</style>
